import {
  OmniElement,
  OmniIconElement,
  OmniLoadingIndicatorElement,
  OmniStyleElement,
  html,
  css,
  classMap,
  nothing,
  debounce,
  ifDefined,
} from 'omni-ui';

import { useHTTP, getEnvironment } from '../helpers/utils.js';
import {
  MicrosoftAuthenticationHandler,
  PreferencesAPI,
  AdaptiveCard,
} from '../services/index.js';

import { openMessageModal } from './omni-teams-message-dialog.js';

import {
  TEAMS_SEARCH_PEOPLE,
  TEAMS_SEARCH_CHAT,
  TEAMS_SEARCH_CHANNEL,
  INPUT_DEBOUNCE_TIMER,
  TEAMS_SEARCH_ALL_CATEGORIES,
  CATEGORY_OPTIONS as teamsCategoryOptions,
  CATEGORY_TO_ABBREVIATION,
  INTIAL_STATE_SEARCH_RESULTS,
} from '../helpers/constants.js';

OmniStyleElement.register();
OmniIconElement.register();
OmniLoadingIndicatorElement.register();

// Don't include Teams Group Chats category for this modal
const CATEGORY_OPTIONS = teamsCategoryOptions.filter(opt => opt !== TEAMS_SEARCH_CHAT);

/**
 * Web component to share objects via MS Teams to individual users or teams
 * @extends OmniElement
 * @param {Object} targetObject - The object to be shared (must have a template)
 * @param {string=} ansid - The authenticated user's session ID
 * @param {string=} userId - The authenticated user's ID
 * @param {Object} client - The name and id of the currently-selected client.
 *   If it's not present, it will be set to the default client name from the
 *   client settings endpoint. **NOTE**: Allowing this to default may cause weird
 *   behavior if teams settings don't exist for the default client.
 * @param {string=} client.name - The name of the current client
 * @param {string=} client.id - The id of the current client
 * @param {string=} env - The environment to reference in API calls
 * @see {@link AdaptiveCard}
 */
export class OmniTeamsSelectionModal extends OmniElement {
  static get properties() {
    return {
      // external propertires
      targetObject: Object,

      // optional
      ansid: String,
      userId: String,
      client: Object,
      env: String,

      // internal state properties
      teamOptions: { type: Array, state: true },
      hasSearchFilter: { type: Boolean, state: true },
      isFetching: { type: Boolean, state: true },
      isLoadingSetup: { type: Boolean, state: true },
      searchResults: { type: Array, state: true },
      selectedRecipients: { type: Object, state: true },
      notificationModal: { type: Object, state: true },
      showAtMenu: { type: Boolean, state: true },
      isFetchingMembers: { type: Boolean, state: true },
      filteredMembers: { type: Array, state: true },
      isSending: { type: Boolean, state: true },
      isSent: { type: Boolean, state: true },
      cardContent: { type: Object, state: true },
      showCategoryDropdown: { type: Boolean, state: true },
      selectedCategories: { type: Object, state: true },
    };
  }

  /**
   * @returns {CSSResult[]}
   */
  static get styles() {
    return [
      super.styles,
      css`
        .modal-card {
          width: 100% !important;
          max-width: 900px !important;
          overflow: auto !important;
        }
        .modal-card-body {
          overflow: visible !important;
        }
        .modal-card-foot {
          justify-content: flex-end !important;
        }
        #panels-container {
          display: flex;
          height: 316px;
        }
        #left-panel,
        #right-panel {
          display: flex;
          flex-direction: column;
          width: 430px;
        }
        #left-panel {
          border-right: 1px solid var(--color-gray-f2);
          padding-right: 15px;
        }
        #right-panel {
          padding-left: 15px;
        }
        /* style category filter dropdown */
        #category-dropdown {
          position: absolute;
          top: 25.5px;
          left: 2px;
          border-radius: 6px 0 0 6px;
          border-right: 1px solid var(--color-gray-db);
          color: rgba(54, 54, 54, 0.3);
        }
        #category-dropdown .dropdown-trigger {
          padding: 0 20px;
          height: 34px;
          width: 173px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        #category-dropdown-shim {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 1;
        }
        #search-input {
          border-radius: 6px !important;
          box-shadow: none;
          height: 36px;
          padding: 6px 48px 6px 193px !important;
          text-overflow: ellipsis;
        }
        /* replicate #search-input :hover and :focus styles to give the illusion that #category-dropdown is inside #search-input */
        #search-container {
          position: relative;
        }
        #search-container:hover #search-input {
          border-color: var(--color-gray-b5);
        }
        #search-container.trigger-input-focus #search-input {
          border-color: var(--color-electric-blue);
          box-shadow: var(--bulma-shadow-1);
          outline: none;
          background-color: var(--color-white);
        }
        #search-input:focus ~ #category-dropdown,
        #search-container.trigger-input-focus #category-dropdown {
          color: var(--color-almost-black);
        }
        #category-dropdown.is-active {
          background-color: var(--color-white);
        }
        .dropdown {
          display: block !important;
          position: absolute;
          margin-left: auto;
          margin-right: auto;
        }
        .dropdown-menu {
          width: 100%;
        }
        #category-dropdown .dropdown-menu {
          width: 191px;
        }
        .dropdown-content {
          max-height: 235px;
          overflow-y: auto;
        }
        omni-loading-indicator::part(svg) {
          width: 35px;
        }
        /* CSS hack to apply style to Safari browser only */
        /* https://browserstrangeness.bitbucket.io/css_hacks.html#safari */
        @media not all and (min-resolution: 0.001dpcm) {
          @supports (-webkit-appearance: none) and (display: flow-root) {
            .dropdown-content {
              position: relative;
            }
          }
        }
        #close-icon,
        #category-chevron-icon,
        #search-icon {
          margin-top: 2px;
          height: 20px;
          width: 20px;
        }
        label {
          display: block;
          font-size: 11px !important;
          text-transform: none !important;
          margin-left: 7px !important;
          margin-bottom: 8px !important;
        }
        #above-message-box {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 8px 7px;
        }
        #above-message-box label {
          margin: 0 !important;
        }
        #members-loading {
          display: inline-flex;
          font-size: 12px;
          white-space: nowrap;
        }
        #members-loading omni-loading-indicator::part(svg) {
          width: 18px;
          margin-right: 12px;
        }
        #search-input ~ .icon {
          z-index: auto !important;
          display: block !important;
          width: auto !important;
          position: absolute;
          top: 31px !important;
          right: 18.5px;
        }
        #close-icon {
          pointer-events: all !important;
          cursor: pointer !important;
        }
        .dropdown-item {
          display: flex !important;
          flex-direction: column;
          justify-content: center;
          align-items: start;
          min-height: 36px;
          cursor: pointer;
          overflow: hidden;
        }
        .dropdown-item:hover {
          background-color: var(--color-pale-grey-two) !important;
        }
        .dropdown-item:active {
          border-color: rgba(213, 243, 252, 0.8) !important;
          background-color: rgba(213, 243, 252, 0.8) !important;
        }
        .dropdown-item p {
          padding: 0 31px;
          font-size: 14px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .search-category-label {
          padding-left: 15px;
          opacity: 0.4;
          height: 36px;
          font-size: 14px;
          cursor: default;
          display: flex;
          flex-direction: column;
          justify-content: center;
          font-weight: 700;
        }
        .disable-button {
          cursor: not-allowed;
        }
        .is-visible {
          visibility: visible !important;
        }
        omni-tooltip {
          max-width: 100%;
        }
        #message-box {
          resize: none;
        }
        #message-box-container {
          position: relative;
        }
        #shadow {
          position: absolute;
          top: 0;
          visibility: hidden;
          pointer-events: none;
          white-space: pre-wrap;
          resize: none;
          letter-spacing: normal; /* #shadow style need to match #message-box */
        }
        /* create custom checkbox */
        .custom-checkbox {
          position: absolute;
          left: 8px;
          height: 12px;
          width: 12px;
          border: 1px solid var(--color-almost-black);
          border-radius: 3px;
        }
        /* style checked custom checkbox */
        .custom-checkbox.is-active {
          background: var(--gradient-electric-blue);
          border: none;
        }
        /* style checkmark */
        .custom-checkbox::after {
          display: none;
          content: '';
          position: absolute;
          left: 4px;
          top: 1px;
          width: 4px;
          height: 8px;
          border: solid white;
          border-width: 0 1px 1px 0;
          transform: rotate(40deg);
        }
        /* show checkmark when checked */
        .custom-checkbox.is-active::after {
          display: block;
        }
        .menu-item > .custom-checkbox {
          left: 22px;
        }
        .dropdown-item span + span {
          margin-left: 20px;
          font-size: 14px;
        }
        .menu-container {
          flex-grow: 1;
          overflow: auto;
          margin-top: 7.5px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
        }
        .results-menu {
          height: 100%;
          width: 100%;
          text-align: left;
        }
        #loading-message {
          margin: 15px 0;
        }
        .hint-message {
          color: var(--color-shark);
        }
        #right-panel .hint-message {
          margin-top: 18px;
        }
        #check-icon {
          position: absolute;
          fill: var(--color-electric-blue);
          margin-right: 4px;
          flex-shrink: 0;
        }
        #mention-dropdown {
          position: absolute;
          z-index: 1;
        }
        #mention-dropdown .dropdown-menu {
          /* need display block inorder to get dropdown offsetWidth */
          display: block;
          visibility: hidden;
          width: auto !important;
        }
        #mention-dropdown.is-active > .dropdown-menu {
          visibility: visible;
        }
        #mention-dropdown .dropdown-content {
          max-height: 196px;
        }
        #mention-dropdown .dropdown-item p {
          padding: 0 18px;
        }
      `,
    ];
  }

  constructor() {
    super();

    this.client = null;
    this.visibleTeams = [];

    this.teamOptions = null;

    this.hasSearchFilter = false;
    this.isFetching = false;
    this.isLoadingSetup = true;
    this.searchResults = INTIAL_STATE_SEARCH_RESULTS;

    this.isFetchingMembers = false;
    this.membersMap = {};
    this.filteredMembers = []; // Filtered Sorted Array by atSearchWord

    /**
     * Note: if showAtMenu is set to false,
     * atIndex & atSearchWord should also be reset to their initial values "null"
     */
    this.showAtMenu = false;
    this.atIndex = null; // index of '@' char
    this.atSearchWord = null; // string after '@' char used to filter members

    this.atMentions = [];

    this.isSending = false;
    this.isSent = false;
    this.cardContent = null;

    this.showCategoryDropdown = false;
    this.selectedCategories = new Set([TEAMS_SEARCH_PEOPLE]);
    this.categoryDropdownLabel = CATEGORY_TO_ABBREVIATION[TEAMS_SEARCH_PEOPLE];
    this.searchInputPlaceholder = `Search ${CATEGORY_TO_ABBREVIATION[TEAMS_SEARCH_PEOPLE]}`;

    this.selectedRecipients = {};

    this.env = this.env ?? getEnvironment();
    this.onSelectRecipient = this.onSelectRecipient.bind(this);
    this.removeRecipient = this.removeRecipient.bind(this);
    this.msAuth = new MicrosoftAuthenticationHandler();
    this.preferencesAPI = new PreferencesAPI(this.env);
  }

  connectedCallback() {
    super.connectedCallback();

    this.ansid = this.ansid || window.AN?.sso?.session?.sid;
    this.userId = this.userId || window.AN?.sso?.user?.personId;
  }

  async firstUpdated() {
    // MS auth requires initialization before it's ever called
    await this.msAuth.init();

    try {
      const { settings: teamsSettings } =
        await this.preferencesAPI.getTeamsPreferences(this.userId, this.ansid);

      // get default client from client settings if it's not set
      if (!this.client) {
        const {
          settings: { default: id = '', defaultClientName: name = '' },
        } = await this.preferencesAPI.getClientPreferences(this.userId, this.ansid);

        this.client = { id, name };
      }

      this.visibleTeams = teamsSettings[this.client.id] || [];

      /**
       * Debounce returns a function in place of itself and that function is invoked the next time the event triggers
       * The debounced function cannot be wrapped in another function's scope because that would invoke debounce with a separate context
       * so, we have 1 eventlistener for the debounced function and another to handle all other operations
       */
      this.inputEl = this.shadowRoot.querySelector('#search-input');
      this.inputEl.addEventListener(
        'input',
        debounce(() => {
          this.getSearchResults();
        }, INPUT_DEBOUNCE_TIMER)
      );

      /**
       * (3/2020) Since Safari doesn't support ShadowRoot Selection API,
       * use textarea selectionStart & selectionEnd to get the caret position,
       * use this.messageBoxShadowEl + textarea selection properties to create Range object
       */
      this.messageBoxEl = this.shadowRoot.querySelector('#message-box');
      this.messageBoxShadowEl = this.shadowRoot.querySelector('#shadow');

      // Reposition suggestion when .modal-card is scrolled or window is resized
      const modalEl = this.shadowRoot.querySelector('.modal-card');
      modalEl.addEventListener('scroll', this.positionMenu.bind(this));
      window.addEventListener('resize', this.positionMenu.bind(this));

      const filterFn = team => this.visibleTeams.includes(team.id);

      this.msAuth
        .getTeams(filterFn)
        .then(res => {
          this.isLoadingSetup = false;
          this.teamOptions = res;
        })
        .catch(error => {
          console.warn('Error getting Teams', error);
          if (error.errorCode === 'user_cancelled') {
            // User cancelled the dialog, so we'll just close the modal.
            this.onClose();
          } else {
            const errorMsg = `We're unable to connect to Teams. Please try again.`;
            this.displayTeamsErrorMessageDialog(errorMsg);
          }
        });

      this.getPeopleResults = filter =>
        this.selectedCategories.has(TEAMS_SEARCH_PEOPLE)
          ? this.msAuth.getPeople(filter)
          : Promise.resolve([]);
      this.getChannelsResults = filter =>
        this.teamOptions.map(({ id, displayName }) =>
          this.selectedCategories.has(TEAMS_SEARCH_CHANNEL)
            ? this.msAuth.getChannels(id, displayName, filter)
            : Promise.resolve([])
        );

      this.categoryToFunc = {
        [TEAMS_SEARCH_PEOPLE]: this.getPeopleResults,
        [TEAMS_SEARCH_CHANNEL]: this.getChannelsResults,
      };

      // The card content is retrieved on loading so we have the future option of displaying a preview.
      this.setCardContent();
    } catch (e) {
      console.log(e);
      const errorMsg =
        "We're currently unable to share to teams Teams. Please try again.";
      this.displayTeamsErrorMessageDialog(errorMsg);

      this.isLoadingSetup = false;
    }
  }

  updated(changed) {
    super.updated(changed);

    if (this.showAtMenu) {
      // Re-position menu if the menu height changed after rendering,
      // This addresses when the menu is positioned on the top side of '@' and the menu shortens as options decrease
      const menuEl = this.shadowRoot.querySelector(
        '#mention-dropdown .dropdown-menu'
      );
      const menuHeight = menuEl.offsetHeight;
      if (this.menuHeight !== menuHeight) {
        this.positionMenu();
      }
    }

    if (changed.has('targetObject')) {
      this.setCardContent();
    }
  }

  async setCardContent() {
    const { targetObject } = this;

    // nothing was passed to be sent
    if (!targetObject) return;

    let url =
      targetObject.uuid_api_connection || targetObject.omni_url
        ? new URL(targetObject.omni_url, window.location.origin).href
        : useHTTP(targetObject.external_url);

    if (targetObject.url) {
      // Condition to use .url when targetObject is a Campaign API object
      url = targetObject.url;
    }

    const targetObjectWithUrl = {
      object_properties: Object.assign(targetObject, {
        url,
      }),
    };

    const cardBuilder = new AdaptiveCard();

    this.cardContent = JSON.parse(cardBuilder.buildCard(targetObjectWithUrl));
  }

  _sendCard = async () => {
    this.isSending = true;
    // Attachments need to have a random id attached, what the id is doesn't matter so this uuid generator's used.
    /* eslint-disable */
    const attachmentId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        let r = (Math.random() * 16) | 0,
          v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
    /* eslint-enable */

    const filteredAtMentions = [];
    // Iterate message-box value to form String message that will be sent to MS API
    let { value } = this.messageBoxEl;
    if (value) {
      let atMentionIdx = -1;
      this.atMentions.forEach(id => {
        // Check to see if @mention still exist
        const member = this.membersMap[id];
        const atMention = `@${member?.displayName}`;
        if (member && value.includes(atMention)) {
          atMentionIdx += 1;
          filteredAtMentions.push({
            id: atMentionIdx,
            userId: member.userId,
            displayName: member.displayName,
          });
          value = value.replace(
            atMention,
            `<at id="${atMentionIdx}">${member.displayName}</at>`
          );
        }
      });
    }

    const cardContent = {
      subject: null,
      body: {
        contentType: 'html',
        content: `${
          value ? `${value}<br><br>` : ''
        }<attachment id="${attachmentId}"></attachment>`,
      },
      mentions: filteredAtMentions.map(({ id, userId, displayName }) => ({
        id,
        mentionText: displayName,
        mentioned: {
          application: null,
          device: null,
          conversation: null,
          user: {
            id: userId,
            displayName,
            userIdentityType: 'aadUser',
          },
        },
      })),
      attachments: [
        {
          id: attachmentId,
          contentType: 'application/vnd.microsoft.card.adaptive',
          contentUrl: null,
          content: JSON.stringify(this.cardContent),
          name: null,
          thumbnailUrl: null,
        },
      ],
    };

    const recipients = Object.values(this.selectedRecipients);
    await Promise.allSettled(
      recipients.map(r => this.msAuth.sendCard(cardContent, r))
    )
      .then(async responses => {
        const notSent = [];
        responses.forEach((resp, idx) => {
          if (resp.status === 'rejected' || resp.value.error) {
            notSent.push(recipients[idx].displayName);
          }
        });
        /*
        Errors at this point are unlikely, but could emerge from malformed cards being provided by the backend not
        adhering to the adaptive card schema.
        */
        if (notSent.length) {
          this.cardContent = ''; // Clearing it out forces a reload of the modal to resend.
          this.isSent = false;

          const errorMsg = `We are not able to send your object and optional message to the following: ${notSent.join(
            '; '
          )}. Please try again.`;
          this.displayTeamsErrorMessageDialog(errorMsg);
        } else {
          this.isSent = true;
          await this.updateComplete;
          this.onSuccess();
        }
      })
      .catch(() => {
        // Catch errors like network connection issues
        const errorMsg =
          "We're unable to connect to Teams. Please click try again.";
        this.displayTeamsErrorMessageDialog(errorMsg);
      });

    this.selectedRecipients = {};
    this.isSending = false;
  };

  onClose = () => {
    this.dispatchNewEvent('teams-modal-close');
  };

  onSuccess = () => {
    this.onClose();
    this.dispatchNewEvent('teams-modal-success', { detail: this.targetObject });
  };

  getSearchResults = async () => {
    // Don't fetch if there's no valid search input
    if (!this.hasSearchFilter) {
      this.isFetching = false;
      return;
    }

    const filter = this.inputEl.value;
    await Promise.all([
      this.getPeopleResults(filter),
      ...this.getChannelsResults(filter),
    ])
      .then(result => {
        const updatedFilter = this.inputEl.value;
        /**
         * getSearchResults is invoked everytime a user stops typing and assigns what is displayed in the dropdown.
         * We only want to assign the results if the current input value is the same as before we started fetching.
         */
        if (filter === updatedFilter) {
          // eslint-disable-next-line prefer-const
          let [people, ...channels] = result;
          channels = channels.flat();

          // Check if there are search results
          if (!people.length && !channels.length) {
            this.searchResults = INTIAL_STATE_SEARCH_RESULTS;
          } else {
            this.searchResults = [
              {
                type: TEAMS_SEARCH_PEOPLE,
                options: people || [],
                visible: true,
              },
              {
                type: TEAMS_SEARCH_CHANNEL,
                options: channels || [],
                visible: true,
              },
            ];
          }

          this.isFetching = false;
        }
      })
      .catch(() => {
        // If error fetching, display error dialog
        const errorMsg =
          "We're having a problem finding your results. Please click try again.";
        this.displayTeamsErrorMessageDialog(errorMsg);
        this.isFetching = false;
      });
  };

  /**
   * Function used to display error message dialog
   * Primary action 'Try again' refreshes the page
   */
  // eslint-disable-next-line class-methods-use-this
  displayTeamsErrorMessageDialog = message => {
    openMessageModal({
      message,
      icon: 'error',
      title: 'Error',
      actionButtonText: 'Try again',
      closeButtonText: 'Dismiss',
      onAction: modal => {
        modal.closeModal(null);
        this.dispatchNewEvent('teams-modal-relaunch');
      },
      onClose: this.onClose,
    });
  };

  toggleCategoryDropdown() {
    this.showCategoryDropdown = !this.showCategoryDropdown;
  }

  closeCategoryDropdown() {
    this.showCategoryDropdown = false;
  }

  /**
   * Handles updating the selectedCategories which includes:
   * updating any UI messages and hide/unhide search results
   * @param {String} option 1 of the category options (people, channels)
   */
  async onCategoryChange(categoryName) {
    let idx;
    if (this.hasSearchFilter) {
      idx = this.searchResults.findIndex(
        results => results.type === categoryName
      );
    }
    // Update selected categories & update the results menu
    if (this.selectedCategories.has(categoryName)) {
      this.selectedCategories.delete(categoryName);
      if (typeof idx === 'number') this.searchResults[idx].visible = false;
    } else {
      this.selectedCategories.add(categoryName);
      if (typeof idx === 'number') {
        this.searchResults[idx].visible = true;
        // If results were never fetched, fetch them now
        if (this.searchResults[idx]?.options.length === 0) {
          this.isFetching = true;
          const filter = this.inputEl.value;
          let results;
          const handleError = () => {
            results = [];
            const errorMsg =
              "We're having a problem finding your results. Please click try again.";
            this.displayTeamsErrorMessageDialog(errorMsg);
          };
          if (categoryName === TEAMS_SEARCH_CHANNEL) {
            await Promise.all([...this.categoryToFunc[categoryName](filter)])
              .then(res => {
                results = res.flat();
              })
              .catch(handleError);
          } else {
            try {
              results = await this.categoryToFunc[categoryName](filter);
            } catch {
              handleError();
            }
          }

          this.searchResults[idx].options = results;
          this.isFetching = false;
        }
      }
    }

    // Update category .dropdown-trigger <span> text & search <input> placeholder text
    if (this.selectedCategories.size === 0) {
      // None selected
      this.categoryDropdownLabel = 'No selection';
      this.searchInputPlaceholder = 'Select filter(s)';
    } else if (this.selectedCategories.size === CATEGORY_OPTIONS.length) {
      // All selected
      this.categoryDropdownLabel = TEAMS_SEARCH_ALL_CATEGORIES;
      this.searchInputPlaceholder = `Search ${TEAMS_SEARCH_ALL_CATEGORIES.toLowerCase()}`;
    } else {
      // Some categories selected
      const categories = [];
      this.selectedCategories.forEach(c => {
        categories.push(CATEGORY_TO_ABBREVIATION[c]);
      });
      const joinedCategories = categories.join(', ');
      this.categoryDropdownLabel = joinedCategories;
      this.searchInputPlaceholder = `Search ${joinedCategories}`;
    }

    // Reassign selectedCategories to trigger update & render
    this.selectedCategories = new Set(this.selectedCategories);
  }

  /**
   * Textarea doesn't come with a way to detect text wrapping
   * Check if the '@' character is wrapped to next row
   */
  didAtWrap() {
    const { value } = this.messageBoxEl;
    const offset = 1; // Range is 1 based indexing

    // Get rect with text up until the '@' character (unwrapped state)
    const textUpToAt = value.substring(0, this.atIndex + offset);
    this.messageBoxShadowEl.textContent = textUpToAt;
    const range = new Range();
    range.setStart(this.messageBoxShadowEl.firstChild, this.atIndex + offset);
    const rect = range.getClientRects()[0];
    const { top } = rect;

    // Get rect when '@' character is followed by searchWord & rest of message (wrapped state)
    this.messageBoxShadowEl.textContent = value;
    const newRange = new Range();
    newRange.setStart(
      this.messageBoxShadowEl.firstChild,
      this.atIndex + offset
    );
    const newRect = newRange.getClientRects()[0];
    const newTop = newRect.top;

    const sameRange = newTop === top;

    return !sameRange;
  }

  // Position suggestion menu
  positionMenu() {
    // Return early if there's no '@' because positioning depends on it
    if (typeof this.atIndex !== 'number') {
      return;
    }

    // Put value into shadow to create range
    this.messageBoxShadowEl.textContent = this.messageBoxEl.value;
    const selRange = new Range();
    const offset = 1; // Range is 1 based indexing
    selRange.setStart(
      this.messageBoxShadowEl.firstChild,
      this.atIndex + offset
    );
    const menuRect = selRange.getClientRects()[0];
    const { left, top: rectTop } = menuRect;
    const top = rectTop - this.messageBoxEl.scrollTop;

    const menuEl = this.shadowRoot.querySelector(
      '#mention-dropdown .dropdown-menu'
    );
    const menuWidth = menuEl.offsetWidth;
    this.menuHeight = menuEl.offsetHeight; // This is the menuHeight before an update
    const { clientWidth, clientHeight } = document.documentElement;

    const menuParentEl = this.shadowRoot.querySelector('#mention-dropdown');
    // Position X
    if (left + menuWidth <= clientWidth) {
      const fontSizeOffset = -14;
      menuParentEl.style.left = `${left + fontSizeOffset}px`;
    } else {
      menuParentEl.style.left = `${left - menuWidth}px`;
    }
    // Position Y
    if (top + this.menuHeight <= clientHeight) {
      const lineHeightOffset = 21;
      menuParentEl.style.top = `${top + lineHeightOffset}px`;
    } else {
      // Menu appears on top of the '@'
      menuParentEl.style.top = `${top - this.menuHeight}px`;
    }
  }

  /**
   * Handles whether or not to show or hide the suggestions menu,
   * finds the position of the '@' character,
   * contains logic for storing @mention/atSearchWord
   * and logic for filtering the suggestion menu (this.filteredMembers)
   * @param {Object} e event
   */
  onMsgInputChange(e) {
    const { selectionEnd, value } = this.messageBoxEl;

    // When the user types '@' but there was an error getting members
    if (e.data === '@' && this.hasMemberError) {
      this.closeAtMenu();
      const errorMsg = `We’re having a problem connecting with Teams @mentions. Please click try again.`;
      this.displayTeamsErrorMessageDialog(errorMsg);
    }

    // Show @ suggestions menu
    if (e.data === '@' && this.filteredMembers.length && !this.showAtMenu) {
      // Save index of '@' character
      this.atIndex = selectionEnd - 1; // convert to zero-based indexing
      this.positionMenu();
      this.showAtMenu = true;
    }

    if (this.showAtMenu) {
      // Close menu if '@' is removed/deleted by checking character at atIndex
      const atChar = value[this.atIndex];
      if (atChar !== '@') {
        this.closeAtMenu();
        this.updateMembers();
      } else {
        // Get searchWord
        const offset = 1; // exclude '@' character
        this.atSearchWord = value.substring(
          this.atIndex + offset,
          selectionEnd
        );
        // Filter @mentions members list with atSearchWord
        this.filteredMembers = this.filteredMembers.filter(({ displayName }) =>
          displayName.toLowerCase().startsWith(this.atSearchWord.toLowerCase())
        );
        // Reset and close menu if there are no members to show
        if (!this.filteredMembers.length) {
          this.closeAtMenu();
          // Reset suggestions in prepartion for next @mention
          this.updateMembers();
        } else {
          // Re-position the menu if the '@' wrapped to the next line
          const didAtWrap = this.didAtWrap();
          if (didAtWrap) {
            this.positionMenu();
          }
        }
      }
    }
  }

  /**
   * Handles the behavior for after a mention suggestion is selected,
   * contains logic for how "@<atSearchWord>" is replaced with @<displayName>
   * and logic for how the caret is positioned after @<displayName>
   * @param {Object} member { displayName }
   */
  onSelectAtMember({ displayName, userId }) {
    const { value } = this.messageBoxEl;

    // Replace @<searchWord> with @<displayName>
    const textBeforeAt = value.substring(0, this.atIndex);
    const offset = 1; // exclude '@'
    const textAfterAt = value.substring(
      this.atIndex + offset + this.atSearchWord.length,
      value.length
    );
    this.messageBoxEl.value = `${textBeforeAt}@${displayName}${textAfterAt} `;

    // Close menu & reset atIndex because '@' was removed
    this.closeAtMenu();
    // Reset suggestions in prepartion for next @mention
    this.updateMembers();

    // Make the caret blink after the @mention
    const positionAfterAt =
      textBeforeAt.length + offset + displayName.length + 1;
    this.messageBoxEl.setSelectionRange(positionAfterAt, positionAfterAt);
    this.messageBoxEl.focus();

    // Save @mentions by Id
    this.atMentions.push(userId);
  }

  onSearchInputChange(e) {
    // Starts showing loader
    this.isFetching = true;

    // When there is a change in input value, we want to make sure the dropdown is ready for new search results so we reset these values
    this.searchResults = INTIAL_STATE_SEARCH_RESULTS;

    // Boolean based on if there is a search input
    const value = e.target.value.trim();
    this.hasSearchFilter = !!value.length;
  }

  clearSearch() {
    this.inputEl.value = '';
    this.hasSearchFilter = false;
  }

  onSelectRecipient(option, type) {
    // Toggle the selected option
    if (this.selectedRecipients[option.id]) {
      this.removeRecipient(option);
    } else {
      this.selectedRecipients[option.id] = { ...option, type };
      // Trigger update immediately following recipients change
      this.requestUpdate();
      // Get list of @mentionable members after user selects recipient
      this.getAtMembers(type, option);
    }
  }

  removeRecipient(option) {
    const removedRecipients = { ...this.selectedRecipients[option.id] };

    delete this.selectedRecipients[option.id];
    this.updateMembers(removedRecipients);
  }

  closeAtMenu() {
    this.showAtMenu = false;
    this.atIndex = null;
    this.atSearchWord = null;
  }

  /**
   * Creates a map of member ids to member info for purpose of
   * removing duplicate members and creating 1 source to access all members
   * & creates a list of members to render to UI
   */
  updateMembers(removedRecipients) {
    // Reset membersMap
    this.membersMap = {};
    // Add all members from all selected recipients
    const recipients = Object.values(this.selectedRecipients);
    recipients.forEach(recipient => {
      // Use optional chaining b/c some recipients are still fetching members
      recipient.members?.forEach(member => {
        if (!this.membersMap[member.userId]) {
          this.membersMap[member.userId] = { ...member };
        }
      });
    });
    // Remove members no longer in the membersMap from the message box
    if (removedRecipients) {
      let { value } = this.messageBoxEl;

      removedRecipients.members.forEach(member => {
        if (!this.membersMap[member.userId]) {
          value = value.replaceAll(`@${member.displayName}`, '').trim();
        }
      });

      this.messageBoxEl.value = value;
    }

    // Sort members alphabetically
    const filteredMembers = Object.values(this.membersMap).sort((a, b) =>
      a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
    );

    this.filteredMembers = filteredMembers;

    if (!this.filteredMembers.length) {
      this.closeAtMenu();
    }
  }

  // Fetch members from API & store in recipient
  async getAtMembers(type, option) {
    this.isFetchingMembers = true;
    this.hasMemberError = false;
    try {
      // Add members to selected recipient of type "Teams" or "Channel"
      // Type "People" already has members data
      if (type === TEAMS_SEARCH_CHANNEL) {
        const teamsMembers = await this.msAuth.getTeamsMembers(option.teamId);
        this.selectedRecipients[option.id].members = teamsMembers;
      }
    } catch {
      this.hasMemberError = true;
    }

    this.updateMembers();
    this.isFetchingMembers = false;
  }

  renderMenu(data, clickEvent) {
    return html`${data.map(({ type, options, visible = true }) => {
      if (options.length && visible === true) {
        return html` <div class="search-category-label">${type}</div>
          ${options.map(
            option => html`
              <li
                class="dropdown-item menu-item"
                tabindex="0"
                role="option"
                aria-selected=${!!this.selectedRecipients[option.id]}
                @click=${() => {
                  clickEvent(option, type);
                }}
                @keydown=${({ key }) => {
                  if (key === 'Enter') clickEvent(option, type);
                }}
              >
                <span
                  class="custom-checkbox ${classMap({
                    'is-active': this.selectedRecipients[option.id],
                  })}"
                ></span>
                ${option.teamName
                  ? html`
                      <omni-tooltip relateby="label">
                        <p slot="invoker">${option.teamName}</p>
                        <div slot="content">${option.teamName}</div>
                      </omni-tooltip>
                    `
                  : nothing}
                <omni-tooltip relateby="label">
                  <p slot="invoker">${option.displayName}</p>
                  <div slot="content">
                    ${option.altTooltip || option.displayName}
                  </div>
                </omni-tooltip>
              </li>
            `
          )}`;
      }

      return nothing;
    })}`;
  }

  renderRecipients() {
    // Create map of category type to all options of that category
    const map = {};
    CATEGORY_OPTIONS.forEach(category => {
      map[category] = { type: category, options: [] };
    });
    // Distribute selectedRecipients into their category type
    Object.values(this.selectedRecipients).forEach(r => {
      map[r.type].options.push(r);
    });
    // Make data the same structure as searchResults
    const data = Object.values(map);

    return this.renderMenu(data, this.removeRecipient);
  }

  /* eslint-disable no-nested-ternary */
  render() {
    const disableSearchInput = this.selectedCategories.size === 0;
    const showSearchResults = !this.isFetching && this.hasSearchFilter;
    const hasRecipients = Object.keys(this.selectedRecipients).length;
    const disableSendButton =
      !hasRecipients || this.isSending || this.isSent || !this.cardContent;
    const hasSearchResults = this.searchResults.some(
      result =>
        this.selectedCategories.has(result.type) && result.options.length > 0
    );
    const showNoResultsMsg =
      !this.isFetching && !hasSearchResults && this.hasSearchFilter;
    /* eslint-disable lit/no-useless-template-literals */
    return html`
      <omni-style>
        <div class="modal is-active">
          <div
            class="modal-background"
            @click="${this.onClose}"
            @keydown=${({ key }) => {
              if (key === 'Enter') this.onClose();
            }}
          ></div>
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Collaborate in Teams</p>
            </header>
            <section class="modal-card-body">
              <div id="panels-container">
                <div id="left-panel">
                  <div
                    id="search-container"
                    class="${classMap({
                      dropdown: true,
                      'trigger-input-focus':
                        (this.showCategoryDropdown || showSearchResults) &&
                        !disableSearchInput,
                      skeleton: this.isLoadingSetup,
                    })}"
                  >
                    <label for="search-input">Search recipients</label>
                    <input
                      id="search-input"
                      type="text"
                      class="input"
                      .disabled=${disableSearchInput}
                      placeholder="${this.searchInputPlaceholder}"
                      @input=${this.onSearchInputChange}
                    />
                    ${this.hasSearchFilter
                      ? html`<span class="icon is-right">
                          <omni-icon
                            id="close-icon"
                            icon-id="omni:interactive:close"
                            @click="${this.clearSearch}"
                          ></omni-icon>
                        </span>`
                      : html`<span class="icon is-right">
                          <omni-icon
                            id="search-icon"
                            icon-id="omni:interactive:search"
                          ></omni-icon>
                        </span>`}
                    <div
                      id="category-dropdown"
                      class="dropdown ${classMap({
                        'is-active': this.showCategoryDropdown,
                      })}"
                    >
                      <div
                        class="control dropdown-trigger"
                        @click=${this.toggleCategoryDropdown}
                        @keydown=${({ key }) => {
                          if (key === 'Enter') this.toggleCategoryDropdown();
                        }}
                      >
                        <span>${this.categoryDropdownLabel}</span>
                        <span class="icon is-right">
                          <omni-icon
                            id="category-chevron-icon"
                            icon-id="omni:interactive:${this
                              .showCategoryDropdown
                              ? 'up'
                              : 'down'}"
                          ></omni-icon>
                        </span>
                      </div>
                      ${this.showCategoryDropdown
                        ? html`<div
                            id="category-dropdown-shim"
                            @click=${this.closeCategoryDropdown}
                            @keydown=${({ key }) => {
                              if (key === 'Enter') this.closeCategoryDropdown();
                            }}
                          ></div>`
                        : nothing}
                      <div class="dropdown-menu">
                        <div class="dropdown-content">
                          ${CATEGORY_OPTIONS.map(
                            option => html`<li
                              class="dropdown-item"
                              @click="${() => this.onCategoryChange(option)}"
                              @keydown=${({ key }) => {
                                if (key === 'Enter')
                                  this.onCategoryChange(option);
                              }}
                            >
                              <span
                                class="${classMap({
                                  'custom-checkbox': true,
                                  'is-active':
                                    this.selectedCategories.has(option),
                                })}"
                              ></span>
                              <span>${option}</span>
                            </li>`
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="menu-container">
                    ${showSearchResults
                      ? showNoResultsMsg
                        ? html`<p>No results found</p>`
                        : html`<ul class="results-menu">
                            ${this.renderMenu(
                              this.searchResults,
                              this.onSelectRecipient
                            )}
                          </ul>`
                      : this.hasSearchFilter && this.isFetching
                      ? html` <div>
                            <omni-loading-indicator></omni-loading-indicator>
                          </div>
                          <p id="loading-message">
                            Searching for recipients&hellip;
                          </p>`
                      : html`
                          <p class="hint-message">
                            Begin searching to display<br />a list of available
                            recipients.
                          </p>
                        `}
                  </div>
                </div>
                <div id="right-panel">
                  <label>Selected recipients</label>
                  <div class="menu-container">
                    ${hasRecipients
                      ? html`<ul class="results-menu">
                          ${this.renderRecipients()}
                        </ul>`
                      : html`
                          <p class="hint-message">
                            No recipients have been added.
                          </p>
                        `}
                  </div>
                </div>
              </div>
              <div id="above-message-box">
                <label for="message-box">Add a message</label>
                ${this.isFetchingMembers
                  ? html`
                      <span id="members-loading">
                        <omni-loading-indicator></omni-loading-indicator>
                        One moment, loading data...
                      </span>
                    `
                  : nothing}
              </div>
              <div
                id="message-box-container"
                class=${classMap({ skeleton: this.isLoadingSetup })}
              >
                <textarea
                  id="message-box"
                  class="textarea"
                  placeholder="Enter a message (optional)"
                  disabled=${ifDefined(
                    this.isFetchingMembers ? true : undefined
                  )}
                  @input="${this.onMsgInputChange}"
                ></textarea>
                <p class="textarea" id="shadow"></p>
              </div>
            </section>
            <footer class="modal-card-foot">
              <button
                class="button is-text is-medium"
                aria-label="close"
                @click="${this.onClose}"
              >
                Cancel
              </button>
              <div class="${classMap({ 'disable-button': disableSendButton })}">
                <button
                  class="button is-primary is-medium"
                  @click="${this._sendCard}"
                  ?disabled="${disableSendButton}"
                >
                  ${this.isSending
                    ? html`<omni-loading-indicator></omni-loading-indicator>`
                    : this.isSent
                    ? html`<omni-icon
                          class="is-size-1"
                          icon-id="omni:informative:check"
                        ></omni-icon>
                        <span>Sent</span>`
                    : html`Send`}
                </button>
              </div>
            </footer>
          </div>
          <div
            id="mention-dropdown"
            class="${classMap({
              dropdown: true,
              'is-active': this.showAtMenu,
            })}"
          >
            <div class="dropdown-menu" role="menu">
              <div class="dropdown-content">
                ${this.filteredMembers.map(
                  m => html`
                    <!-- TODO: aria-role/selected here is a little strange -->
                    <li
                      class="dropdown-item"
                      tabindex="0"
                      role="option"
                      aria-selected="false"
                      @click=${() => {
                        this.onSelectAtMember(m);
                      }}
                      @keydown=${({ key }) => {
                        if (key === 'Enter') this.onSelectAtMember(m);
                      }}
                    >
                      <p>${m.displayName}</p>
                    </li>
                  `
                )}
              </div>
            </div>
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('omni-teams-selection-modal', OmniTeamsSelectionModal);
